<script lang="ts" setup>
  import { Library } from "~/entities/Library";
  import type { LibraryContent } from "~/entities/LibraryContent";
  import { getPublishStatusLabel } from "~/consts/contentsConfig";

  interface Props {
    content: LibraryContent;
    selectedLibrary: Library;
    index: number;
    parentIndex?: number;
    isLast?: boolean;
  }
  defineProps<Props>();

  defineEmits(["clickLibraryContent"]);

  const isTablet = isTabletAndSpDisplayRef();

  const getLengthByType = (contents: LibraryContent[], types: string[]) => {
    return contents.filter(content => types.includes(content.contentsType)).length;
  };
</script>
<template>
  <div
    class="list-item"
    :class="{
      complete: content.isComplete && !selectedLibrary.isComplete,
      'partial-complete': content.isComplete && selectedLibrary.isComplete,
    }"
    @click="$emit('clickLibraryContent', content)"
  >
    <!-- number / status -->
    <div class="list-item-number">
      <span v-if="!content.isComplete" class="number">{{
        parentIndex === undefined ? `${index + 1}` : `${parentIndex + 1}-${index + 1}`
      }}</span>
      <AtomsMenuIcon v-if="content.isComplete" :width="20" :height="20" class="mx-auto" icon="circle-check-blue" />
      <span v-if="!isLast" class="seperator"></span>
    </div>
    <!-- thumbnail -->
    <div class="list-item-thumbnail" :class="parentIndex !== undefined ? 'ml-4' : ''">
      <AtomsImgWithLoading
        :width="isTablet ? 100 : 140"
        :height="isTablet ? 58 : 78"
        :src="content.thumbnailImage"
        contains
        class="rounded"
      />
    </div>
    <template v-if="content.isDirectory">
      <!-- directory -->
      <div class="list-item-content">
        <!-- title -->
        <div class="list-item-title mb-1">
          <v-icon v-if="content.isOpenDirectory" icon="$mdiFolderOpen" color="white" />
          <v-icon v-else icon="$mdiFolder" color="white" />
          <span class="text-truncate text">
            {{ content.contentsTitle }}
          </span>
        </div>
        <div class="list-item-duration-directory ml-1">
          <AtomsTypeIcon class="icon" :width="14" type="movie" />
          <span> {{ getLengthByType(content.directoryContents, ["movie", "archive_movie"]) }} </span>
          <AtomsTypeIcon class="icon" :width="14" type="document" />
          <span> {{ getLengthByType(content.directoryContents, ["document"]) }} </span>
          <AtomsTypeIcon class="icon" :width="14" type="article" />
          <span> {{ getLengthByType(content.directoryContents, ["article"]) }} </span>
        </div>

        <div class="progress-wrapper d-flex align-center ga-2">
          <div class="flex-grow-1 progress-linear mt-1">
            <v-progress-linear color="blue" :model-value="content.progress" :height="6" />
          </div>
          <div v-if="!content.isComplete" class="progress-text">{{ content.progress }}%</div>
          <div v-else class="progress-complete">
            <span> 閲覧済 </span>
          </div>
        </div>

        <div v-if="content.publishStatus !== 'PUBLISH' && !content.isDirectory" class="private">
          <span>{{ getPublishStatusLabel(content.publishStatus) }}</span>
        </div>
      </div>
    </template>
    <template v-else>
      <!-- contents -->
      <div class="list-item-content">
        <!-- title -->
        <div class="list-item-title mb-1">
          <AtomsTypeIcon class="icon" :type="content.contentsType" />
          <span v-if="content.contentsType == 'archive_movie'" class="text-truncate text">
            アーカイブ | {{ content.contentsTitle }}
          </span>
          <span v-if="content.contentsType !== 'archive_movie'" class="text-truncate text">
            {{ content.contentsTitle }}
          </span>
        </div>
        <div class="list-item-duration">
          <span v-if="content.moviePlayTimeSec">
            {{ formatMoviePlayTime(content.moviePlayTimeSec, true) }}
          </span>
          <span v-if="content.documentTotalPage"> {{ content.documentTotalPage }}ページ </span>
        </div>

        <div class="progress-wrapper d-flex align-center ga-2">
          <div class="flex-grow-1 progress-linear mt-1">
            <v-progress-linear color="blue" :model-value="content.progress" :height="6" />
          </div>
          <div v-if="!content.isComplete" class="progress-text">{{ content.progress }}%</div>
          <div v-else class="progress-complete">
            <span v-if="['movie', 'archive_movie'].includes(content.contentsType)"> 視聴済 </span>
            <span v-if="['document', 'article'].includes(content.contentsType)"> 閲覧済 </span>
          </div>
        </div>

        <div v-if="content.publishStatus !== 'PUBLISH'" class="private">
          <span>{{ getPublishStatusLabel(content.publishStatus) }}</span>
        </div>
      </div>
    </template>
  </div>
</template>
<style lang="scss" scoped>
  @import "assets/media.scss";
  .list-item {
    display: flex;
    padding-bottom: 10px;
    cursor: pointer;
    height: 88px;
    position: relative;
  }
  .private {
    position: absolute;
    height: 78px;
    width: calc(100% - 48px);
    top: 0;
    right: 0;
    background-color: rgb(var(--v-theme-monochrome-08), 0.8);
    span {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 14px;
      color: white;
      padding: 2px 20px 4px 20px;
      border-radius: 30px;
      background-color: rgb(var(--v-theme-monochrome-04));
    }
  }
  .list-item-number {
    width: 48px;
    text-align: center;
    color: white;
    text-align: center;
    margin-top: -5px;
    .number {
      font-size: 20px;
      font-weight: 700;
    }
    .seperator {
      display: block;
      height: 50px;
      width: 1px;
      background: #ffffff38;
      margin: 0 auto;
      margin-top: 8px;
    }
  }
  .list-item-thumbnail {
    width: 140px;
    height: 78px;
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
    overflow: hidden;
    @include display_tablet {
      background-color: rgb(var(--v-theme-monochrome-06));
      width: 100px;
      padding: 10px 0;
    }
  }
  .list-item-content {
    background: rgb(var(--v-theme-monochrome-04));
    padding: 5px 10px 0 10px;
    height: 78px;
    flex-grow: 1;
    overflow: hidden;
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
  }
  .list-item-duration-directory {
    display: flex;
    align-items: center;
    gap: 4px;
    height: 12px;
    font-size: 12px;
    font-weight: 400;
    .icon {
      width: 12px;
      height: 12px;
    }
  }
  .list-item-duration {
    font-size: 12px;
    font-weight: 400;
    color: rgb(var(--v-theme-monochrome-02));
  }
  .list-item-title {
    display: flex;
    align-items: center;
    gap: 4px;
    height: 22px;
    overflow: hidden;
    .icon {
      width: 22px;
      height: 22px;
    }
    .text {
      flex-grow: 1;
      height: 22px;
      font-size: 16px;
      font-weight: 700;
      width: calc(100vw - 248px - 400px);
      max-width: calc(1280px - 248px - 400px);
      @include display_tablet {
        font-size: 14px;
        width: calc(100vw - 360px);
      }
    }
  }
  .progress-linear {
    border-radius: 10px;
    overflow: hidden;
  }
  .progress-text {
    font-size: 13px;
    font-weight: 700;
    color: rgb(var(--v-theme-primary));
  }
  .progress-complete {
    font-size: 12px;
    font-weight: 400;
  }
  .complete {
    .list-item-content {
      opacity: 0.6;
    }
  }
  .partial-complete {
    .list-item-duration {
      opacity: 0.6;
    }
    .progress-wrapper {
      opacity: 0.6;
    }
  }
</style>
